import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { useRestaurant } from '../hooks/useRestaurant';
import { KotTicket } from '../types';
import { printKotUsb } from '../services/receipt';
import { formatTime, formatDate } from '../services/datetime';
import './Sections.css';
import './Restaurant.css';

// KOT Report (kot_report_layout.xml): date-filtered KOT list with Today /
// Yesterday / Custom tabs, a Waiter Summary view, per-ticket breakdown,
// CANCEL KOT for active tickets, single-KOT reprint, and a footer total.
type RangeKey = 'today' | 'yesterday' | 'custom';
type View = 'list' | 'waiter';

const KotReport: React.FC = () => {
  const { formatMoney, config } = usePos();
  const rest = useRestaurant();
  const [range, setRange] = useState<RangeKey>('today');
  const [view, setView] = useState<View>('list');
  const [customDate, setCustomDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reprint, setReprint] = useState<KotTicket | null>(null);

  const dayKey = (iso: string) => new Date(iso).toISOString().split('T')[0];
  const todayKey = new Date().toISOString().split('T')[0];
  const yesterdayKey = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  const targetKey = range === 'today' ? todayKey : range === 'yesterday' ? yesterdayKey : customDate;

  const filtered = useMemo(
    () => rest.kots.filter((k) => dayKey(k.timestamp) === targetKey).sort((a, b) => b.kotNo - a.kotNo),
    [rest.kots, targetKey]
  );

  const active = filtered.filter((k) => k.status !== 'cancelled');
  const activeTotal = active.reduce((s, k) => s + k.amount, 0);
  const totalQty = active.reduce((s, k) => s + k.lines.reduce((q, l) => q + l.quantity, 0), 0);

  // Waiter Summary: aggregate active KOTs by waiter.
  const waiterRows = useMemo(() => {
    const map = new Map<string, { kots: number; qty: number; amount: number }>();
    active.forEach((k) => {
      const key = k.waiter?.trim() || 'Unassigned';
      const r = map.get(key) ?? { kots: 0, qty: 0, amount: 0 };
      r.kots += 1;
      r.qty += k.lines.reduce((q, l) => q + l.quantity, 0);
      r.amount += k.amount;
      map.set(key, r);
    });
    return Array.from(map.entries()).sort((a, b) => b[1].amount - a[1].amount);
  }, [active]);

  return (
    <div className="section-page rest-page">
      <div className="page-header">
        <h1>KOT REPORT</h1>
      </div>

      <div className="kot-tabs">
        <button className={range === 'today' ? 'active' : ''} onClick={() => setRange('today')}>
          Today
        </button>
        <button className={range === 'yesterday' ? 'active' : ''} onClick={() => setRange('yesterday')}>
          Yesterday
        </button>
        <button className={range === 'custom' ? 'active' : ''} onClick={() => setRange('custom')}>
          Date Custom
        </button>
        {range === 'custom' && (
          <input type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} />
        )}
        <span style={{ width: 16 }} />
        <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
          KOT List
        </button>
        <button className={view === 'waiter' ? 'active' : ''} onClick={() => setView('waiter')}>
          Waiter Summary
        </button>
        <button className="btn-grey" style={{ marginLeft: 'auto', borderRadius: 20 }} onClick={() => window.print()}>
          Print
        </button>
      </div>

      {/* ---- Waiter summary view ---- */}
      {view === 'waiter' ? (
        waiterRows.length === 0 ? (
          <p className="empty-hint">No KOTs for this date.</p>
        ) : (
          <table className="section-table">
            <thead>
              <tr>
                <th>Waiter</th>
                <th className="num-cell">KOTs</th>
                <th className="num-cell">Qty</th>
                <th className="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {waiterRows.map(([name, r]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{r.kots}</td>
                  <td>{r.qty}</td>
                  <td className="right">{formatMoney(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : filtered.length === 0 ? (
        <p className="empty-hint">No KOTs for this date.</p>
      ) : (
        /* ---- KOT list view ---- */
        filtered.map((k) => {
          const qty = k.lines.reduce((q, l) => q + l.quantity, 0);
          const open = expanded === k.id;
          return (
            <div key={k.id} className={`kot-card ${k.status === 'cancelled' ? 'cancelled' : ''}`}>
              <div className="kot-card-head" onClick={() => setExpanded(open ? null : k.id)}>
                <span className="kot-no">KOT #{k.kotNo}</span>
                <span className="kot-table">{k.tableName}</span>
                <span className="kot-waiter">
                  {k.waiter || '—'} · {formatTime(k.timestamp)} · {k.lines.length} item
                  {k.lines.length === 1 ? '' : 's'} · Qty {qty}
                </span>
                <span className={`kot-status ${k.status}`}>{k.status}</span>
                <span className="right" style={{ minWidth: 80, fontWeight: 700 }}>
                  {formatMoney(k.amount)}
                </span>
                <button
                  className="kot-reprint-btn"
                  title="Reprint KOT"
                  onClick={(e) => {
                    e.stopPropagation();
                    setReprint(k);
                  }}
                >
                  🖨
                </button>
                {k.status === 'active' && (
                  <button
                    className="kot-cancel-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Cancel KOT #${k.kotNo}?`)) rest.cancelKot(k.id);
                    }}
                  >
                    CANCEL KOT
                  </button>
                )}
              </div>
              {open && (
                <div className="kot-card-lines">
                  {k.lines.map((l, i) => (
                    <div className="kl" key={i}>
                      <span>
                        {l.name} × {l.quantity}
                        {l.remark && <em className="kl-remark"> — {l.remark}</em>}
                      </span>
                      <span>{formatMoney(l.unitPrice * l.quantity)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}

      {view === 'list' && filtered.length > 0 && (
        <div className="kot-foot">
          <span>Total Qty: {totalQty}</span>
          <span>Active KOT Amount: {formatMoney(activeTotal)}</span>
        </div>
      )}

      {/* ---- Single KOT reprint ---- */}
      {reprint && (
        <div className="modal-overlay" onClick={() => setReprint(null)}>
          <div className="kot-print-modal" onClick={(e) => e.stopPropagation()}>
            <div className="kot-ticket" id="kot-ticket-print">
              <h3>{config.storeName}</h3>
              <div className="kt-meta">
                <strong>KOT #{reprint.kotNo}</strong>
                {reprint.status === 'cancelled' && <span className="kt-void"> (CANCELLED)</span>}
              </div>
              <div className="kt-meta">
                Table: {reprint.tableName} &nbsp; Waiter: {reprint.waiter || '—'}
              </div>
              <div className="kt-meta">
                {formatDate(reprint.timestamp)} {formatTime(reprint.timestamp)}
              </div>
              <hr />
              {reprint.lines.map((l, i) => (
                <div className="kt-line" key={i}>
                  <span>
                    {l.quantity} × {l.name}
                    {l.remark && <em> ({l.remark})</em>}
                  </span>
                </div>
              ))}
              <hr />
              <div className="kt-line kt-total">
                <span>Total Qty</span>
                <span>{reprint.lines.reduce((q, l) => q + l.quantity, 0)}</span>
              </div>
            </div>
            <div className="kot-print-actions">
              <button
                className="btn-teal"
                onClick={async () => {
                  // Prefer the USB kitchen printer; fall back to browser print.
                  const sent = await printKotUsb(reprint, config.storeName).catch(() => false);
                  if (!sent) window.print();
                }}
              >
                Print
              </button>
              <button className="btn-grey" onClick={() => setReprint(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KotReport;
